import React, { useEffect, useMemo, useState } from 'react';

import { toProjectDateOnly, toUtcDeadline, toUtcStart } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import { useQuery } from '@tanstack/react-query';
import { listProjectCompanies, type ProjectCompanyItem } from 'src/features/projects/companiesApi';
import type { Contract } from 'src/types/contract.type';

/**
 * Contract 생성/수정용 입력(payload) 타입
 * - Phase 2 API (POST /projects/:projectId/contracts) 스키마에 맞춘다.
 */
export type CreateContractInput = {
    direction: 'CLIENT' | 'SUBCON';
    projectCompanyId: string; // UUID
    contractName: string;
    contractAmount: number;
    currency?: string | null; // ISO3 e.g. KRW, USD
    startDate?: string | null; // UTC ISO (Z)
    endDate?: string | null; // UTC ISO (Z)
    contractDate?: string | null; // UTC ISO (Z)
};

function toDirection(v: any): 'CLIENT' | 'SUBCON' {
    if (v === 'VENDOR') return 'SUBCON';
    return v === 'SUBCON' ? 'SUBCON' : 'CLIENT';
}

function normIso3(s?: any) {
    return String(s ?? '')
        .trim()
        .toUpperCase();
}

const EXCLUDED_NON_CLIENT_ROLES = new Set<string>([]);

const CURRENCY_OPTIONS = [
    'KRW',
    'USD',
    'EUR',
    'JPY',
    'CNY',
    'GBP',
    'CHF',
    'HKD',
    'SGD',
    'AUD',
    'CAD',
    'TWD',
    'THB',
    'VND',
    'INR',
    'IDR',
    'MYR',
    'PHP',
    'SAR',
    'AED',
    'QAR',
    'KWD',
] as const;

export default function ContractEditorModal({
    open,
    onClose,
    initial,
    projectId,
    onSave,
    canEdit,
}: {
    open: boolean;
    onClose: () => void;
    initial?: Partial<Contract>;
    projectId: string;
    onSave: (input: CreateContractInput) => void;
    canEdit?: boolean;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [draft, setDraft] = useState<CreateContractInput | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const { data: pcs = [] } = useQuery<ProjectCompanyItem[]>({
        queryKey: ['projectCompanies', projectId],
        queryFn: () => listProjectCompanies(projectId),
        staleTime: 30_000,
        enabled: !!projectId && open,
    });

    useEffect(() => {
        if (!open) return;

        const direction = toDirection(initial?.direction);
        const next: CreateContractInput = {
            direction,
            projectCompanyId: (initial as any)?.projectCompanyId ?? '',
            contractName: initial?.contractName ?? '',
            contractAmount: Number(initial?.contractAmount ?? 0),
            currency: normIso3(initial?.currency ?? 'KRW') || 'KRW',
            startDate: initial?.startDate ? toProjectDateOnly(initial.startDate, zone) : null,
            endDate: initial?.endDate ? toProjectDateOnly(initial.endDate, zone) : null,
            contractDate: initial?.contractDate ? toProjectDateOnly(initial.contractDate, zone) : null,
        };

        setDraft(next);
        setFormError(null);
    }, [open, initial, zone]);

    const companyOptions = useMemo(() => {
        if (!draft) return [];

        if (draft.direction === 'CLIENT') {
            return pcs.filter((pc) => pc.role === 'CLIENT');
        }

        return pcs.filter((pc) => pc.role !== 'CLIENT' && !EXCLUDED_NON_CLIENT_ROLES.has(pc.role));
    }, [draft, pcs]);

    if (!open || !draft) return null;

    const onField = <K extends keyof CreateContractInput>(k: K, v: CreateContractInput[K]) => {
        setDraft((prev) => (prev ? ({ ...prev, [k]: v } as CreateContractInput) : prev));
        if (formError) setFormError(null);
    };

    const validate = (): string | null => {
        if (!draft.projectCompanyId) return '상대 회사를 선택해 주세요.';
        if (!draft.contractName.trim()) return '계약명을 입력해 주세요.';
        if (!Number.isFinite(draft.contractAmount) || draft.contractAmount <= 0) return '계약 금액을 입력해 주세요.';
        if (!draft.contractDate) return '계약 날짜를 입력해 주세요.';
        if (!draft.startDate) return '계약 시작일을 입력해 주세요.';
        if (!draft.endDate) return '계약 종료일을 입력해 주세요.';
        if (draft.currency && !/^[A-Z]{3}$/.test(normIso3(draft.currency))) return '통화는 ISO 3자리 코드여야 합니다.';
        return null;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                className="p-3 w-full max-w-3xl rounded-2xl bg-white border shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-3 border-b text-sm font-medium">{initial?.id ? '계약 편집' : '계약 생성'}</div>

                <div className="p-3 space-y-6 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Direction</div>
                            <select
                                className="w-full rounded border p-2"
                                value={draft.direction}
                                onChange={(e) => onField('direction', toDirection(e.target.value))}
                                disabled={!canEdit}
                            >
                                <option value="CLIENT">CLIENT (수입)</option>
                                <option value="SUBCON">SUBCON (지출)</option>
                            </select>
                        </label>

                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Currency</div>
                            <select
                                className="w-full rounded border p-2"
                                value={normIso3(draft.currency ?? 'KRW') || 'KRW'}
                                onChange={(e) => onField('currency', normIso3(e.target.value))}
                                disabled={!canEdit}
                            >
                                {CURRENCY_OPTIONS.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <label className="space-y-1 block">
                        <div className="text-xs text-gray-500">상대 회사 (ProjectCompany)</div>
                        <select
                            className="w-full rounded border p-2"
                            value={draft.projectCompanyId}
                            onChange={(e) => onField('projectCompanyId', e.target.value)}
                            disabled={!canEdit}
                        >
                            <option value="">선택...</option>
                            {companyOptions.map((pc) => (
                                <option key={pc.id} value={pc.id}>
                                    {pc.company?.name ?? pc.id}
                                </option>
                            ))}
                        </select>

                        {companyOptions.length === 0 && (
                            <div className="text-[11px] text-amber-700">
                                {draft.direction === 'CLIENT'
                                    ? 'Settings에서 CLIENT ProjectCompany를 먼저 등록해 주세요.'
                                    : 'Settings에서 SUB_CONTRACTOR ProjectCompany를 먼저 등록해 주세요.'}
                            </div>
                        )}
                    </label>

                    <label className="space-y-1 block">
                        <div className="text-xs text-gray-500">Contract Name</div>
                        <input
                            className="w-full rounded border p-2"
                            value={draft.contractName}
                            onChange={(e) => onField('contractName', e.target.value)}
                            disabled={!canEdit}
                        />
                    </label>

                    <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Contract Amount</div>
                            <input
                                type="number"
                                className="w-full rounded border p-2"
                                value={Number.isFinite(draft.contractAmount) ? String(draft.contractAmount) : ''}
                                onChange={(e) =>
                                    onField('contractAmount', e.target.value === '' ? 0 : Number(e.target.value))
                                }
                                disabled={!canEdit}
                            />
                        </label>

                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Signed Date</div>
                            <input
                                type="date"
                                className="w-full rounded border p-2"
                                value={draft.contractDate ?? ''}
                                onChange={(e) => onField('contractDate', e.target.value || null)}
                                disabled={!canEdit}
                            />
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Start Date</div>
                            <input
                                type="date"
                                className="w-full rounded border p-2"
                                value={draft.startDate ?? ''}
                                onChange={(e) => onField('startDate', e.target.value || null)}
                                disabled={!canEdit}
                            />
                        </label>

                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">End Date</div>
                            <input
                                type="date"
                                className="w-full rounded border p-2"
                                value={draft.endDate ?? ''}
                                onChange={(e) => onField('endDate', e.target.value || null)}
                                disabled={!canEdit}
                            />
                        </label>
                    </div>

                    {formError ? (
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                            {formError}
                        </div>
                    ) : null}
                </div>

                <div className="mt-6 p-3 border-t flex items-center justify-end gap-3">
                    <button className="rounded px-4 py-1.5 text-sm border hover:bg-gray-50" onClick={onClose}>
                        Cancel
                    </button>

                    <button
                        className="rounded px-4 py-1.5 text-sm bg-slate-900 text-white hover:bg-gray-700 disabled:opacity-50"
                        disabled={!canEdit}
                        onClick={() => {
                            const err = validate();
                            if (err) {
                                setFormError(err);
                                return;
                            }

                            const payload: CreateContractInput = {
                                ...draft,
                                currency: normIso3(draft.currency ?? 'KRW') || 'KRW',
                                contractDate: draft.contractDate ? toUtcStart(draft.contractDate, zone) : null,
                                startDate: draft.startDate ? toUtcStart(draft.startDate, zone) : null,
                                endDate: draft.endDate ? toUtcDeadline(draft.endDate, zone) : null,
                            };

                            onSave(payload);
                            onClose();
                        }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
